import { useCallback, useMemo, useRef } from 'react';

export interface Store<T = object> {
  get: () => T;
  set: (value: T) => void;
  subscribe: (callback: () => void) => () => void;
}

function isEqual(prev: any, current: any): boolean {
  return prev === current;
}

export function useCreateStore<K = object>(
  initialData: K,
  isEqualCheck = isEqual
): Store<K> {
  const storeRef = useRef<K>(initialData);
  const subscribers = useRef(new Set<() => void>());

  const get = useCallback(() => {
    return storeRef.current;
  }, []);

  const set = useCallback(
    (data: K) => {
      if (isEqualCheck(storeRef.current, data)) return;
      storeRef.current = data;
      // Defer subscriber notifications so set() can run during render
      // (e.g. FormFieldsContent syncing Formik props) without triggering
      // "Cannot update a component while rendering a different component".
      const subs = [...subscribers.current];
      queueMicrotask(() => {
        subs.forEach((callback) => callback());
      });
    },
    [isEqualCheck]
  );

  const subscribe = useCallback((callback: () => void) => {
    subscribers.current.add(callback);
    return () => subscribers.current.delete(callback);
  }, []);

  return useMemo(
    () => ({
      get,
      set,
      subscribe,
    }),
    [get, set, subscribe]
  );
}

export function subscribeMany(
  stores: Store<any>[],
  callback: () => any
): () => void {
  const unsubscribeArray = stores.map((store) =>
    store.subscribe(callback)
  );
  return () => {
    unsubscribeArray.forEach((unsubscribe) => unsubscribe());
  };
}

export interface CleanupCollector {
  add: (cleanup: () => void) => void;
  cleanup: () => void;
}

export function createCleanupCollector(): CleanupCollector {
  const cleanups: (() => void)[] = [];

  return {
    add: (cleanup) => cleanups.push(cleanup),
    cleanup: () => {
      cleanups.forEach((fn) => fn());
      cleanups.length = 0;
    },
  };
}
